import { useState, useCallback } from 'react'
import { useAuthStore } from '../auth/authStore'
import { KBLayout } from './components/KBLayout'
import { getSectionsForRole } from './data/sections'
import {
  DashboardOverview,
  DashboardKpi,
  DashboardFunnel,
  DashboardEvents,
} from './sections/manager/DashboardSection'
import {
  NotificationsBell,
  NotificationsFeed,
  NotificationsTypes,
  NotificationsMentions,
  NotificationsNews,
} from './sections/manager/NotificationsSection'
import {
  ClientsList,
  ClientsNew,
  ClientsDetail,
  ClientsInn,
} from './sections/manager/ClientsSection'
import { RequestsList, RequestsNew, RequestsDetail } from './sections/manager/RequestsSection'
import {
  RequestsStatuses,
  RequestsItems,
  RequestsAssign,
} from './sections/manager/RequestsStatusesSection'
import {
  ProcurementRequestsOverview,
  ProcurementRequestsCreate,
  ProcurementRequestsImport,
} from './sections/manager/ProcurementRequestsSection'
import {
  KpMode,
  KpPrices,
  KpQuotesSelection,
  KpMarkup,
  KpPreview,
  KpExport,
} from './sections/manager/KpSection'
import {
  OrdersOverview,
  OrdersInvoicesPayments,
  OrdersDocumentsHistory,
  OrdersRolesApproval,
} from './sections/manager/OrdersSection'
import {
  CatalogOverview,
  CatalogNaming,
  CatalogLearning,
  CatalogUnfExport,
} from './sections/manager/CatalogSection'
import {
  SuppliersOverview,
  SuppliersCard,
  SuppliersCleanup,
  SuppliersMerge,
  SuppliersImport,
} from './sections/procurement/SuppliersSection'
import {
  AnalyticsOverview,
  AnalyticsSummaryTab,
  AnalyticsPeopleTabs,
  AnalyticsKpTimeline,
} from './sections/admin/AnalyticsSection'
import {
  PlanningPlanFactTable,
  PlanningAkb,
  PlanningRating,
} from './sections/admin/PlanningSection'
import {
  TableOverview,
  TableFilters,
  TableGroups,
  TableColumns,
  SettingsAccount,
} from './sections/manager/TableSettingsSection'
import {
  QueueOverview,
  QueueFilters,
  QueueState,
  QueueTake,
  QueueCompletion,
  QueueManufacturer,
  QueueHide,
} from './sections/procurement/QueueSection'
import { ProcDashboardOverview, ProcDashboardStats } from './sections/procurement/DashboardSection'
import {
  ProcRequestsList,
  ProcRequestsDetail,
  ProcRequestsNotes,
  ProcQuotesCreate,
  ProcQuotesEdit,
} from './sections/procurement/RequestsQuotesSection'
import { RolesOverview, RolesRopRos, RolesCities } from './sections/admin/RolesSection'
import {
  AdminAccountsList,
  AdminAccountsCreate,
  AdminRatesEdit,
  AdminRatesMarkup,
  AdminCatalogOverview,
  AdminCatalogImport,
} from './sections/admin/AdminSections'

const SECTION_COMPONENTS: Record<string, React.ComponentType> = {
  'dashboard-overview': DashboardOverview,
  'dashboard-kpi': DashboardKpi,
  'dashboard-funnel': DashboardFunnel,
  'dashboard-events': DashboardEvents,
  'notifications-bell': NotificationsBell,
  'notifications-feed': NotificationsFeed,
  'notifications-types': NotificationsTypes,
  'notifications-mentions': NotificationsMentions,
  'notifications-news': NotificationsNews,
  'clients-list': ClientsList,
  'clients-new': ClientsNew,
  'clients-detail': ClientsDetail,
  'clients-inn': ClientsInn,
  'requests-list': RequestsList,
  'requests-new': RequestsNew,
  'requests-detail': RequestsDetail,
  'requests-statuses': RequestsStatuses,
  'requests-items': RequestsItems,
  'requests-assign': RequestsAssign,
  'procurement-requests-overview': ProcurementRequestsOverview,
  'procurement-requests-create': ProcurementRequestsCreate,
  'procurement-requests-import': ProcurementRequestsImport,
  'kp-mode': KpMode,
  'kp-prices': KpPrices,
  'kp-quotes-selection': KpQuotesSelection,
  'kp-markup': KpMarkup,
  'kp-preview': KpPreview,
  'kp-export': KpExport,
  'orders-overview': OrdersOverview,
  'orders-invoices-payments': OrdersInvoicesPayments,
  'orders-documents-history': OrdersDocumentsHistory,
  'orders-roles-approval': OrdersRolesApproval,
  'catalog-overview': CatalogOverview,
  'catalog-naming': CatalogNaming,
  'catalog-learning': CatalogLearning,
  'catalog-unf-export': CatalogUnfExport,
  'table-overview': TableOverview,
  'table-filters': TableFilters,
  'table-groups': TableGroups,
  'table-columns': TableColumns,
  'settings-account': SettingsAccount,
  'proc-dashboard-overview': ProcDashboardOverview,
  'proc-dashboard-stats': ProcDashboardStats,
  'proc-queue-overview': QueueOverview,
  'proc-queue-filters': QueueFilters,
  'proc-queue-state': QueueState,
  'proc-queue-take': QueueTake,
  'proc-queue-completion': QueueCompletion,
  'proc-queue-manufacturer': QueueManufacturer,
  'proc-queue-hide': QueueHide,
  'proc-requests-list': ProcRequestsList,
  'proc-requests-detail': ProcRequestsDetail,
  'proc-requests-notes': ProcRequestsNotes,
  'proc-quotes-create': ProcQuotesCreate,
  'proc-quotes-edit': ProcQuotesEdit,
  'suppliers-overview': SuppliersOverview,
  'suppliers-card': SuppliersCard,
  'suppliers-cleanup': SuppliersCleanup,
  'suppliers-merge': SuppliersMerge,
  'suppliers-import': SuppliersImport,
  'analytics-overview': AnalyticsOverview,
  'analytics-summary': AnalyticsSummaryTab,
  'analytics-people-tabs': AnalyticsPeopleTabs,
  'analytics-kp-timeline': AnalyticsKpTimeline,
  'planning-plan-fact-table': PlanningPlanFactTable,
  'planning-akb': PlanningAkb,
  'planning-rating': PlanningRating,
  'admin-roles-overview': RolesOverview,
  'admin-roles-rop-ros': RolesRopRos,
  'admin-roles-cities': RolesCities,
  'admin-accounts-list': AdminAccountsList,
  'admin-accounts-create': AdminAccountsCreate,
  'admin-rates-edit': AdminRatesEdit,
  'admin-rates-markup': AdminRatesMarkup,
  'admin-catalog-overview': AdminCatalogOverview,
  'admin-catalog-import': AdminCatalogImport,
}

export function KnowledgeBasePage() {
  const role = useAuthStore((s) => s.user?.role ?? 'MANAGER')
  const sections = getSectionsForRole(role)
  const defaultId = sections[0]?.subsections[0]?.id ?? ''
  const [activeId, setActiveId] = useState(defaultId)

  const handleNavigate = useCallback((id: string) => {
    setActiveId(id)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const Component = SECTION_COMPONENTS[activeId]

  return (
    <div className="space-y-0">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">База знаний</h1>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Обучающие материалы по работе с CRM
          {(role === 'ADMIN' || role === 'GENERAL_DIRECTOR') && ' · Видны все разделы системы'}
        </p>
      </div>

      <KBLayout sections={sections} activeId={activeId} onNavigate={handleNavigate}>
        {Component ? (
          <Component />
        ) : (
          <div className="py-12 text-center text-gray-400">Раздел в разработке</div>
        )}
      </KBLayout>
    </div>
  )
}
